import { api } from "@/lib/apiClient";
import type { AbsenceDayMarker, AbsenceStatus, AbsenceType } from "@/lib/employeeAbsences";

export interface HeroEmployeeAbsence {
  id: number | null;
  hero_type: string | null;
  absence_type: AbsenceType;
  status: AbsenceStatus;
  start: string;
  end: string;
  total_days?: number | null;
  comment: string | null;
}

export interface HeroEmployeeCalendar {
  year: number;
  partner_id: number | null;
  partner_name: string | null;
  linked: boolean;
  hours_by_day: Record<string, number>;
  absences: HeroEmployeeAbsence[];
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1);
}

function formatIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function expandHeroAbsenceDays(absences: HeroEmployeeAbsence[]): Map<string, AbsenceDayMarker[]> {
  const byDay = new Map<string, AbsenceDayMarker[]>();

  for (const absence of absences) {
    if (absence.status === "rejected") continue;
    const start = parseIsoDate(absence.start);
    const end = parseIsoDate(absence.end);
    const cursor = new Date(start);
    while (cursor <= end) {
      const iso = formatIsoDate(cursor);
      const markers = byDay.get(iso) ?? [];
      markers.push({
        date: iso,
        absence_type: absence.absence_type,
        status: absence.status,
        day_fraction: 1,
        half_day_part: null,
      });
      byDay.set(iso, markers);
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return byDay;
}

export function heroHoursByDayMap(hoursByDay: Record<string, number>): Map<string, number> {
  const map = new Map<string, number>();
  for (const [iso, hours] of Object.entries(hoursByDay)) {
    const value = Number(hours);
    if (value > 0) map.set(iso, value);
  }
  return map;
}

export async function getHeroEmployeeCalendar(params: {
  year: number;
  user_login?: string;
}): Promise<HeroEmployeeCalendar> {
  const query = new URLSearchParams({ year: String(params.year) });
  if (params.user_login) query.set("user_login", params.user_login);
  return api.get<HeroEmployeeCalendar>(`/employee-times/hero-calendar?${query.toString()}`);
}

export function filterHeroAbsencesForMonth(
  absences: HeroEmployeeAbsence[],
  year: number,
  month: number,
): HeroEmployeeAbsence[] {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  return absences.filter((absence) => {
    const start = absence.start.slice(0, 7);
    const end = absence.end.slice(0, 7);
    return start <= prefix && end >= prefix;
  });
}

export function sumHeroHoursInMonth(
  hoursByDay: Map<string, number>,
  year: number,
  month: number,
): number {
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  let total = 0;
  for (const [iso, hours] of hoursByDay) {
    if (iso.startsWith(prefix)) total += hours;
  }
  return total;
}

export function sumHeroHoursInYear(hoursByDay: Map<string, number>, year: number): number {
  const prefix = `${year}-`;
  let total = 0;
  for (const [iso, hours] of hoursByDay) {
    if (iso.startsWith(prefix)) total += hours;
  }
  return total;
}

export function formatHeroAbsenceRange(absence: HeroEmployeeAbsence): string {
  const start = absence.start.slice(0, 10);
  const end = absence.end.slice(0, 10);
  if (start === end) {
    return new Date(`${start}T12:00:00`).toLocaleDateString("de-DE");
  }
  const from = new Date(`${start}T12:00:00`).toLocaleDateString("de-DE");
  const to = new Date(`${end}T12:00:00`).toLocaleDateString("de-DE");
  return `${from} – ${to}`;
}
